import { UserService, waitFor } from "@frontiers-vue3/public-pages";
import type { ILoggedUserInfo } from "@frontiers-vue3/public-pages";
import type { IResearchTopic } from "~/types";
import { getOnlinePrimaryOrDefaultSection } from "~/helpers/getOnlinePrimaryOrDefaultSection";
import type {
  IDataLayerAppInfo,
  IDataLayerUserInfo,
  IDataLayerViewRT
} from "~/types/tracking";

export default defineNuxtPlugin((nuxtApp) => {
  function injectGtmScript(): void {
    const d = document;
    const s = "script";
    const l = "dataLayer";

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      "event": "gtm.js"
    });

    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s) as HTMLScriptElement;
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = `${gtmServerUrl}/gtm.js?id=${gtmId}${dl}&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x`;

    if (f.parentNode) {
      f.parentNode.insertBefore(j, f);
    }
  }

  function setDataLayerAppId(): void {
    const appInfo: IDataLayerAppInfo = {
      app_id: spaceId
    };
    pushDataLayerVariables(appInfo);
  }

  async function setDataLayerUserInfo(): Promise<void> {
    const userService = new UserService();
    try {
      const loggedUserInfo: ILoggedUserInfo | undefined =
        await userService.getLoggedUserInfo();
      if (loggedUserInfo) {
        const loopData: IDataLayerUserInfo = {
          loopId: loggedUserInfo.id.toString()
        };
        pushDataLayerVariables(loopData);
      }
    } catch (err) {
      console.error(`Error getting from userInfo for datalayer - ${err}`);
    }
  }

  function pushDataLayerVariables(
    dataLayerItem:
      | IDataLayerAppInfo
      | IDataLayerUserInfo
      | IDataLayerViewRT
      | Record<string, unknown>
  ): void {
    window.dataLayer.push(dataLayerItem);
  }

  function setViewResearchTopicEvent(currentRT: IResearchTopic): void {
    const primaryJournal = currentRT.journals.find((j) => j.isPrimary);
    if (!primaryJournal) {
      throw new Error(
        `Researh topic #${currentRT.id} does not have a primary journal.`
      );
    }

    const onlinePrimaryOrDefaultSection = getOnlinePrimaryOrDefaultSection(
      primaryJournal?.sections
    );

    const viewJournalEvent: IDataLayerViewRT = {
      event: "view_research_topic",
      journal_id: primaryJournal.id.toString(),
      journal_title: primaryJournal.title,
      section_id: onlinePrimaryOrDefaultSection?.sectionId.toString() || "",
      section_title: onlinePrimaryOrDefaultSection?.title || "",
      research_topic_id: currentRT.id.toString(),
      research_topic_title: currentRT.title
    };

    pushDataLayerVariables(viewJournalEvent);
  }

  const { spaceId, gtmServerUrl, gtmId, gtmAuth, gtmPreview, isDevMode } =
    useRuntimeConfig().public;
  const { currentResearchTopic } = useMain();

  window.dataLayer = window.dataLayer || [];

  nuxtApp.hook("page:finish", async () => {
    setDataLayerAppId();
    await setDataLayerUserInfo();
    injectGtmScript();

    waitFor(
      () => {
        const otFound = window.dataLayer.find(
          (e) => e.event === "OneTrustGroupsUpdated"
        );
        return otFound !== undefined;
      },
      50,
      100,
      !isDevMode
    ).then(() => setViewResearchTopicEvent(currentResearchTopic));
  });
});
